import React, { useState } from "react";
import { toLocalISODate } from "../lib/dates";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertTriangle, Download } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { CollapsibleList } from "./CollapsibleList";

export function DeleteMemberButton({ member, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = name.trim().toLowerCase() === (member.name || "").trim().toLowerCase();
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/members/${member.id}/delete`, { confirm_name: name, reason });
      toast.success(`${member.name} moved to the deleted bin — restore within 30 days`);
      setOpen(false); onDeleted?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" className="w-full gap-2 border-destructive/50 text-destructive hover:bg-destructive/10" onClick={() => setOpen(true)} data-testid="delete-member-open"><Trash2 size={15} /> Delete member</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="delete-member-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle size={18} /> Delete {member.name}?</DialogTitle></DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Login is blocked immediately and all devices are signed out.</li>
            <li>Hidden from the directory, roster, reports and attendance.</li>
            <li>Payments, welfare and audit history are kept for the books.</li>
            <li>Held for <b>30 days</b> in the deleted bin — an admin can restore in that time. After that the record is permanently removed.</li>
          </ul>
          <div><Label>Reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Test account, duplicate, left the association" data-testid="delete-member-reason" /></div>
          <div><Label>Type the member's full name to confirm: <span className="font-mono-accent text-crimson">{member.name}</span></Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="delete-member-confirm" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="delete-member-cancel">Cancel</Button>
            <Button variant="destructive" disabled={!ok || busy} onClick={go} data-testid="delete-member-submit">{busy ? "Deleting…" : "Delete & hold 30 days"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PurgeDialog({ member, onClose, onDone }) {
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.delete(`/admin/deleted-members/${member.id}`, { data: { confirm: txt } }); toast.success(`${member.name} permanently deleted`); onDone(); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="purge-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle size={18} /> Permanently delete {member?.name}?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">This cannot be undone. The member record, sessions, passkeys, notifications and RSVPs are removed now. Payment, welfare and audit entries stay with the name recorded as text.</p>
        <div><Label>Type <span className="font-mono-accent text-crimson">DELETE</span> to confirm</Label><Input value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="purge-confirm" /></div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="purge-cancel">Cancel</Button>
          <Button variant="destructive" disabled={txt !== "DELETE" || busy} onClick={go} data-testid="purge-submit">{busy ? "Deleting…" : "Delete permanently"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeletedMembers({ onChanged }) {
  const { data, reload } = useApi("/admin/deleted-members");
  const [purging, setPurging] = useState(null);
  const restore = async (m) => {
    try { await api.post(`/admin/deleted-members/${m.id}/restore`); toast.success(`${m.name} restored`); reload(); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const done = () => { reload(); onChanged?.(); };
  if (!data) return null;
  return (
    <div className="mb-8" data-testid="deleted-members">
      <CollapsibleList id="deleted-members" title="Deleted members (30-day hold)" rows={data} defaultOpen={data.length > 0}
        searchable matcher={(m, t) => `${m.name} ${m.email} ${m.member_id} ${m.deleted_by} ${m.delete_reason}`.toLowerCase().includes(t)}
        placeholder="Search deleted…" empty="No members are waiting in the deleted bin."
        right={data.length > 0 && <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadPdf("/admin/deleted-members.csv", "deleted-members.csv")} data-testid="deleted-export"><Download size={14} /> CSV</Button>}
        render={(m) => (
          <div key={m.id} className="glass flex flex-wrap items-center justify-between gap-3 rounded-xl p-3 text-sm" data-testid={`deleted-${m.id}`}>
            <div>
              <div className="font-medium">{m.name} <span className="font-mono-accent text-xs text-muted-foreground">{m.member_id} · {m.email || "no email"} · {m.role}</span></div>
              <div className="text-xs text-muted-foreground">Deleted {toLocalISODate(m.deleted_at)} by {m.deleted_by}{m.delete_reason && ` — “${m.delete_reason}”`} · <b className={m.days_left <= 5 ? "text-destructive" : "text-crimson"}>{m.days_left} day(s) left</b> (purge {toLocalISODate(m.purge_at)})</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="gap-1 border-palm text-palm" onClick={() => restore(m)} data-testid={`restore-${m.id}`}><RotateCcw size={14} /> Restore</Button>
              <Button size="sm" variant="destructive" className="gap-1" onClick={() => setPurging(m)} data-testid={`purge-${m.id}`}><Trash2 size={14} /> Delete permanently</Button>
            </div>
          </div>
        )} />
      <PurgeDialog member={purging} onClose={() => setPurging(null)} onDone={done} />
    </div>
  );
}
